import React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import Linkify from 'react-linkify';
import Modal from '../../common/Modal';
import i18n from '../../core/i18n';
import { requestDataIfNeeded } from '../../updates/user-updates/duck';
import * as API from '../../core/api';

const renderActionText = (action, count, hasSomeJoinEvents) => {
  const texts = {
    JOIN_PROJECT: () =>
      i18n.t('events.project.join', {
        count,
        postProcess: 'interval',
      }),
    UNJOIN_PROJECT: () => {
      if (hasSomeJoinEvents) {
        return i18n.t('events.project.unjoinWithMultiple', {
          count,
          postProcess: 'interval',
        });
      } else {
        return i18n.t('events.project.unjoin', {
          count,
          postProcess: 'interval',
        });
      }
    },
    ADD_PROJECT_MEMO: () => i18n.t('events.project.addMemo'),
  };

  return texts[action] ? texts[action]() : '';
};

const separators = {
  DEFAULT: ['[', ']'],
  SIDE: ['{', '}'],
};

const classColors = {
  DEFAULT: 'u-color-blue-violet',
  SIDE: 'u-color-shakespeare',
};

const displayProjectNameOnMemoIfNeeded = (events, project) => {
  const shouldDisplayProjectName = events.every(
    ({ action }) => action === 'ADD_PROJECT_MEMO'
  );

  if (!shouldDisplayProjectName) {
    return null;
  }

  return (
    <React.Fragment>
      {` ${i18n.t('events.project.projectsMemoOwn')} `}
      <Link
        to={`/projects/${project._id}`}
        className={`c-box-primary__link c-box-primary__link ${
          classColors[project.type]
        }`}
      >
        {`${separators[project.type][0]}${project.name}${
          separators[project.type][1]
        }`}
      </Link>
    </React.Fragment>
  );
};

const renderProjectMembers = (events, project, action) => {
  const filteredEvents = events.filter((e) => e.action === action);

  if (!filteredEvents.length) {
    return null;
  }

  const items = filteredEvents
    .map((e) => (
      <Link
        key={e.createdAt}
        to={`/users/${e.initiator._id}`}
        className="c-box-primary__link"
      >
        {e.initiator.name}
      </Link>
    ))
    // render react components by using map and join
    // {@link https://stackoverflow.com/a/35840806/1333836}
    .reduce((accu, elem, currentIndex, array) => {
      const separator =
        elem === array[array.length - 1] ? ` ${i18n.t('general.and')} ` : ', ';

      return accu === null ? [elem] : [...accu, separator, elem];
    }, null);

  const hasSomeJoinEvents = events.some(
    ({ action }) => action === 'JOIN_PROJECT'
  );

  const shouldNotDisplayProjectName =
    action === 'UNJOIN_PROJECT' && hasSomeJoinEvents;
  const shouldDisplayProjectName = !shouldNotDisplayProjectName;

  return (
    <React.Fragment>
      {items}
      {` ${renderActionText(
        action,
        filteredEvents.length,
        hasSomeJoinEvents
      )} `}
      {shouldDisplayProjectName && (
        <Link
          to={`/projects/${project._id}`}
          className={`c-box-primary__link c-box-primary__link ${
            classColors[project.type]
          }`}
        >
          {`${separators[project.type][0]}${project.name}${
            separators[project.type][1]
          }`}
        </Link>
      )}
      <br />
    </React.Fragment>
  );
};

const renderDelete = (onClick) => (
  <div className="u-margin-left">
    <button onClick={onClick} className="c-btn  c-btn--delete" />
  </div>
);

const ItemProject = ({ isCurrentUser, handleModalOpen, events, project }) => (
  <div className="c-box-primary__box">
    <div className="c-box-primary__box u-display-flex u-justify-content-between u-align-items-center u-padding-none">
      <div className="c-box-primary__text">
        {renderProjectMembers(events, project, 'JOIN_PROJECT')}

        {renderProjectMembers(events, project, 'UNJOIN_PROJECT')}

        {events.filter((e) => e.action === 'ADD_PROJECT_MEMO').map((event) => (
          <div
            className={`${events.length === 1 ? '' : 'u-margin-top'}`}
            key={event.createdAt}
          >
            <Link
              to={`/users/${event.initiator._id}`}
              className="c-box-primary__link"
            >
              {event.initiator.name}
            </Link>{' '}
            {i18n.t('events.project.sharedMemo')}
            {displayProjectNameOnMemoIfNeeded(events, project)}:
            <blockquote
              className={
                'c-blockquote u-font-italic u-margin-top display-linebreak'
              }
            >
              <Linkify
                properties={{
                  target: '_blank',
                  className: 'u-color-blue-violet',
                }}
              >
                {event.data.memo.content}
              </Linkify>
            </blockquote>
          </div>
        ))}
      </div>

      {isCurrentUser && renderDelete(handleModalOpen)}
    </div>
  </div>
);

class Item extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isModalOpen: false,
    };
  }

  handleEventDelete = () => {
    const { events, requestUserEventsDataIfNeeded } = this.props;
    const event = events[0];

    API.del(`events/${event._id}`)
      // Immediately refresh all user events
      .then(this.handleModalClose)
      .then(requestUserEventsDataIfNeeded);
  };

  handleModalOpen = () => {
    this.setState({ isModalOpen: true });
  };

  handleModalClose = () => {
    this.setState({ isModalOpen: false });
  };

  render() {
    const { project, isCurrentUser, events } = this.props;
    const { isModalOpen } = this.state;

    return (
      <React.Fragment>
        <ItemProject
          events={events}
          project={project}
          isCurrentUser={isCurrentUser}
          handleModalOpen={this.handleModalOpen}
        />

        {isCurrentUser && (
          <Modal
            isOpen={isModalOpen}
            onRequestClose={this.handleModalClose}
            title={i18n.t('manageUpdates.remove.projectModalTitle')}
          >
            <p className="u-weight-light">
              {i18n.t('manageUpdates.remove.projectModalNote')}
            </p>
            <div className="u-text-center u-margin-bottom u-margin-top-huge">
              <div className="u-display-flex u-justify-content-around u-flex-wrap u-padding-horizontal-huge">
                <button
                  className="c-btn c-btn-outline u-margin-vertical-micro"
                  onClick={this.handleModalClose}
                >
                  {i18n.t('general.cancel')}
                </button>
                <button
                  className="c-btn c-btn-outline c-btn-outline--primary u-margin-vertical-micro"
                  onClick={this.handleEventDelete}
                >
                  {i18n.t('general.delete')}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = {
  requestUserEventsDataIfNeeded: requestDataIfNeeded,
};

export default connect(
  null,
  mapDispatchToProps
)(Item);



// WEBPACK FOOTER //
// ./src/events/projects/Item.js